"use client";

import { useEffect } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Download, Lock, RotateCcw, ExternalLink, History, Share2 } from "lucide-react";
import { useCheckout } from "@/hooks/use-checkout";
import { toast } from "sonner";
import { isPromotionEnabled, getEffectiveCredits, getBaseCredits, PREVIEW_CTA_PACK_ID, getPackPriceUsd, type CreditPackId } from "@/lib/payments/client";
import { MiniPreviewOffer } from "@/components/app/mini-preview-offer";
import { trackFreeExit, trackUpgradePromptShown as trackUpgradePromptShownGA } from "@/lib/analytics";
import {
  trackDownloadGateClicked,
  trackImageDownloaded,
  trackUnlockClicked,
  trackUpgradePromptShown,
  trackFreePreviewOfferShown,
  trackFreePreviewPaidCtaClicked,
} from "@/lib/posthog-client";
import { GenerationHistoryDrawer } from "@/components/app/generation-history-drawer";
import { type GenerationWithVehicle } from "@/hooks/use-generations";
import {
  FREE_PREVIEW_OFFER_VARIANT,
  getCompletedFreePreviewNumber,
} from "@/lib/payments/free-preview-config";

// Client-safe trial pack price (in cents) - REMOVED
// const TRIAL_PRICE_CENTS: number = 149; // Removed

interface ResultsViewProps {
  originalImageUrl: string;
  resultImageUrl: string;
  onTryAnother: () => void;
  isFreePreview?: boolean;
  isUnlocked?: boolean;
  generationId?: string;
  onUnlock?: () => void;
  creditBalance?: number;
  hasPurchasedAnyPack?: boolean;
  remainingFreePreviews?: number;
  onSelectGeneration?: (generation: GenerationWithVehicle) => void;
  selectedGenerationId?: string | null;
  wheelInfo?: {
    name: string;
    imageUrl: string;
    brand?: string;
    sku?: string;
    isCustom: boolean;
  } | null;
  generationDate?: Date | null;
}

export function ResultsView({
  resultImageUrl,
  onTryAnother,
  isFreePreview = false,
  isUnlocked = true,
  generationId,
  onUnlock,
  creditBalance = 0,
  remainingFreePreviews = 0,
  onSelectGeneration,
  selectedGenerationId,
  wheelInfo,
  generationDate,
}: ResultsViewProps) {
  const checkout = useCheckout();
  const isLocked = isFreePreview && !isUnlocked;
  const canUnlock = creditBalance >= 1;
  const isPromoEnabled = isPromotionEnabled(PREVIEW_CTA_PACK_ID);
  const promoCredits = getEffectiveCredits(PREVIEW_CTA_PACK_ID);
  const paidPreviewsAfterUnlock = Math.max(promoCredits - 1, 0);
  const baseCredits = getBaseCredits(PREVIEW_CTA_PACK_ID);
  const ctaPackPrice = getPackPriceUsd(PREVIEW_CTA_PACK_ID);
  const previewNumber = getCompletedFreePreviewNumber(remainingFreePreviews);

  useEffect(() => {
    if (!isLocked) return;

    trackUpgradePromptShownGA({
      trigger: "after_generation",
      isFreePreview,
    });
    trackUpgradePromptShown({
      generationId,
      trigger: "after_generation",
      isFreePreview,
      source: "results_view",
    });
    trackFreePreviewOfferShown({
      generationId,
      previewNumber,
      remainingFreePreviews,
      source: "results_view",
      offerVariant: FREE_PREVIEW_OFFER_VARIANT,
    });
  }, [generationId, isFreePreview, isLocked, previewNumber, remainingFreePreviews]);
  
  const handleDownload = () => {
    if (isLocked) {
      // Track free exit: download attempt blocked
      trackFreeExit({
        exitPoint: "download_attempt",
        ...(generationId && { generationId }),
      });
      trackDownloadGateClicked({
        generationId,
        isFreePreview,
        isUnlocked,
        source: "results_view",
      });
      return; // Disabled for locked free previews
    }
    try {
      if (!generationId) {
        toast.error("Unable to download this preview yet");
        return;
      }
      // Track download in PostHog
      trackImageDownloaded({
        generationId,
        imageType: "result",
        isFreePreview,
        isUnlocked,
      });
      // proper download handling for all devices using API proxy
      const downloadUrl = `/api/download?generationId=${encodeURIComponent(generationId)}`;
      window.location.href = downloadUrl;
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleUnlock = () => {
    // Track free exit: unlock attempt
    if (isLocked) {
      trackFreeExit({
        exitPoint: "unlock_attempt",
        ...(generationId && { generationId }),
      });
      // Track unlock click in PostHog
      trackUnlockClicked({
        generationId,
        creditBalance,
      });
    }
    if (onUnlock) {
      onUnlock();
    }
  };

  const handlePurchase = (packId: CreditPackId = PREVIEW_CTA_PACK_ID) => {
    trackFreePreviewPaidCtaClicked({
      packId,
      generationId,
      previewNumber,
      remainingFreePreviews,
      source: "results_view",
      offerVariant: FREE_PREVIEW_OFFER_VARIANT,
    });
    checkout.mutate(
      {
        packId,
        selectedGenerationId: generationId,
        source: "results_view",
        previewNumber,
      },
      {
        onError: (error) => {
          console.error("Checkout error:", error);
          toast.error(error instanceof Error ? error.message : "Failed to start checkout");
        },
      }
    );
  };

  const handleTryAnother = () => {
    onTryAnother();
  };


  const handleContextMenu = (e: React.MouseEvent) => {
    if (isLocked) {
      e.preventDefault();
    }
  };

  const handleOpenInNewTab = () => {
    window.open(resultImageUrl, '_blank', 'noopener,noreferrer');
  };

  const handleShare = () => {
    const url = window.location.href;

    if (navigator.share) {
      navigator
        .share({
          title: "Check out this wheel preview",
          url,
        })
        .catch(() => {
          // Ignore share cancellation or errors
        });
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          alert("Link copied to clipboard");
        })
        .catch(() => {
          alert(url);
        });
    } else {
      alert(url);
    }
  };

  // Unlocked state - show download option
  if (!isLocked) {
    return (
      <div>
        {/* Mobile: Full-width image with icon toolbar */}
        <div className="lg:hidden rounded-t-xl overflow-hidden">
          <div className="relative bg-black">
            <Image
              src={resultImageUrl}
              alt="Car with new rims"
              width={1200}
              height={900}
              className="w-full h-auto"
              draggable
            />
            {/* Icon toolbar overlay */}
            <div className="absolute top-3 right-3 flex items-center gap-2">
              {onSelectGeneration && (
                <GenerationHistoryDrawer
                  onSelect={onSelectGeneration}
                  selectedId={selectedGenerationId}
                >
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
                    title="View history"
                  >
                    <History className="w-5 h-5" />
                  </Button>
                </GenerationHistoryDrawer>
              )}
              <Button
                variant="secondary"
                size="icon"
                className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
                onClick={handleTryAnother}
                title="Try another wheel"
              >
                <RotateCcw className="w-5 h-5" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
                onClick={handleOpenInNewTab}
                title="View fullscreen"
              >
                <ExternalLink className="w-5 h-5" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
                onClick={handleShare}
                title="Share"
              >
                <Share2 className="w-5 h-5" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="h-10 w-10 bg-primary hover:bg-primary/90 text-white border-0"
                onClick={handleDownload}
                title="Download"
              >
                <Download className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Desktop: Full image with icon toolbar overlay */}
        <div className="hidden lg:block">
          <div className="relative overflow-hidden">
            <Image
              src={resultImageUrl}
              alt="Car with new rims"
              width={1200}
              height={900}
              className="w-full h-auto"
              draggable
            />
            {/* Icon toolbar overlay - always visible */}
            <div className="absolute top-3 right-3 flex items-center gap-2">
              {onSelectGeneration && (
                <GenerationHistoryDrawer
                  onSelect={onSelectGeneration}
                  selectedId={selectedGenerationId}
                >
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
                    title="View history"
                  >
                    <History className="w-5 h-5" />
                  </Button>
                </GenerationHistoryDrawer>
              )}
              <Button
                variant="secondary"
                size="icon"
                className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
                onClick={handleTryAnother}
                title="Try another wheel"
              >
                <RotateCcw className="w-5 h-5" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
                onClick={handleOpenInNewTab}
                title="View fullscreen"
              >
                <ExternalLink className="w-5 h-5" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
                onClick={handleShare}
                title="Share"
              >
                <Share2 className="w-5 h-5" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="h-10 w-10 bg-primary hover:bg-primary/90 text-white border-0"
                onClick={handleDownload}
                title="Download"
              >
                <Download className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Wheel Information Section */}
        {wheelInfo && (
          <div className="mt-4 bg-zinc-900/50 border border-white/10 rounded-xl p-4 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-white/5 shrink-0">
                <Image
                  src={wheelInfo.imageUrl}
                  alt={wheelInfo.name}
                  fill
                  sizes="64px"
                  className="object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-zinc-500 mb-1">
                  {wheelInfo.isCustom ? "Custom Wheel" : "Catalog Wheel"}
                </p>
                <p className="text-sm font-medium text-white truncate">
                  {wheelInfo.name}
                </p>
                {(wheelInfo.brand || wheelInfo.sku) && (
                  <p className="text-xs text-zinc-400 mt-1">
                    {[wheelInfo.brand, wheelInfo.sku].filter(Boolean).join(" • ")}
                  </p>
                )}
              </div>
              {generationDate && (
                <div className="text-right shrink-0">
                  <p className="text-xs text-zinc-500 mb-1">Generated</p>
                  <p className="text-xs font-medium text-white">
                    {new Date(generationDate).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric"
                    })}
                  </p>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    {new Date(generationDate).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit"
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Handle deleted wheel case */}
        {wheelInfo === null && (
          <div className="mt-4 bg-zinc-900/50 border border-white/10 rounded-xl p-4 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg bg-white/5 shrink-0 flex items-center justify-center">
                <span className="text-2xl">❌</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-zinc-400">
                  Wheel (deleted)
                </p>
                <p className="text-xs text-zinc-500 mt-1">
                  This wheel has been removed from your library
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Locked free preview - show upsell flow
  return (
    <div className="space-y-6 pb-64 lg:pb-6">
      {/* Mobile: Full-width image with icon toolbar */}
      <div className="lg:hidden rounded-t-xl overflow-hidden">
        <div className="relative bg-black">
          <Image
            src={resultImageUrl}
            alt="Car with new rims"
            width={1200}
            height={900}
            className="w-full h-auto"
            draggable={false}
            onContextMenu={handleContextMenu}
          />
          {/* Icon toolbar overlay */}
          <div className="absolute top-3 right-3 flex items-center gap-2">
            {onSelectGeneration && (
              <GenerationHistoryDrawer
                onSelect={onSelectGeneration}
                selectedId={selectedGenerationId}
              >
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
                  title="View history"
                >
                  <History className="w-5 h-5" />
                </Button>
              </GenerationHistoryDrawer>
            )}
          </div>
        </div>
      </div>

      {/* Desktop: Full image with icon toolbar overlay */}
      <div className="hidden lg:block">
        <div className="relative overflow-hidden">
          <Image
            src={resultImageUrl}
            alt="Car with new rims"
            width={1200}
            height={900}
            className="w-full h-auto"
            draggable={false}
            onContextMenu={handleContextMenu}
          />

          {/* Icon toolbar overlay - always visible */}
          <div className="absolute top-3 right-3 flex items-center gap-2">
            {onSelectGeneration && (
              <GenerationHistoryDrawer
                onSelect={onSelectGeneration}
                selectedId={selectedGenerationId}
              >
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
                  title="View history"
                >
                  <History className="w-5 h-5" />
                </Button>
              </GenerationHistoryDrawer>
            )}
          </div>
        </div>
      </div>

      {/* Wheel Information Section */}
      {wheelInfo && (
        <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-4 backdrop-blur-sm mx-4 lg:mx-0">
          <div className="flex items-center gap-4">
            <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-white/5 shrink-0">
              <Image
                src={wheelInfo.imageUrl}
                alt={wheelInfo.name}
                fill
                sizes="64px"
                className="object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-zinc-500 mb-1">
                {wheelInfo.isCustom ? "Custom Wheel" : "Catalog Wheel"}
              </p>
              <p className="text-sm font-medium text-white truncate">
                {wheelInfo.name}
              </p>
              {(wheelInfo.brand || wheelInfo.sku) && (
                <p className="text-xs text-zinc-400 mt-1">
                  {[wheelInfo.brand, wheelInfo.sku].filter(Boolean).join(" • ")}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Handle deleted wheel case */}
      {wheelInfo === null && (
        <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-4 backdrop-blur-sm mx-4 lg:mx-0">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg bg-white/5 shrink-0 flex items-center justify-center">
              <span className="text-2xl">❌</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-zinc-400">
                Wheel (deleted)
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                This wheel has been removed from your library
              </p>
            </div>
          </div>
        </div>
      )}

      {/* MOBILE VERSION - Optimized for Ad Conversions */}
      <div className="lg:hidden space-y-4 px-4">
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-center">
          <p className="text-sm font-semibold text-emerald-300">
            Your free preview is ready
          </p>
          <p className="mt-1 text-xs text-emerald-100/80">
            Unlock this result to remove the watermark, download it in high resolution, and create {paidPreviewsAfterUnlock} more previews.
          </p>
        </div>

        {/* Limited Time Offer Badge */}
        {isPromoEnabled && (
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-linear-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40 text-amber-300 text-sm font-semibold">
              🎁 Limited Time Offer
            </div>
            <p className="text-base font-semibold text-foreground">
              Get 20% bonus credits today
            </p>
            <p className="text-sm text-muted-foreground">
              Unlock HD previews and get extra credits for free.
            </p>
          </div>
        )}

        {/* Value Micro-copy */}
        <p className="text-center text-xs font-medium text-emerald-400">
          HD previews • No watermark • More generations
        </p>
      </div>

      {/* DESKTOP VERSION */}
      <div className="hidden lg:block space-y-6 max-w-4xl mx-auto">
        {/* Promotional Badge - More Visible */}
        {isPromoEnabled && (
          <div className="text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-linear-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40 text-amber-300 text-sm font-semibold shadow-lg shadow-amber-500/10">
              🎁 Limited-time bonus: +20% previews
            </span>
          </div>
        )}

        {/* Simple Value Block */}
        <div className="text-center">
          <div className="mb-4 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4">
            <p className="text-sm font-semibold text-emerald-300">
              Your free preview is ready
            </p>
            <p className="mt-1 text-xs text-emerald-100/80">
              Unlock this result to remove the watermark, download it in high resolution, and create {paidPreviewsAfterUnlock} more previews.
            </p>
          </div>
          <p className="text-lg font-medium text-emerald-400">
            HD previews • No watermark • More generations
          </p>
        </div>

        {/* Primary CTA - Hidden on mobile (shown in sticky footer) */}
        <div className="hidden lg:block space-y-3">
          {canUnlock ? (
            <Button 
              onClick={handleUnlock} 
              size="lg" 
              className="w-full"
            >
              <Lock className="w-4 h-4 mr-2" />
              Unlock and Download
            </Button>
          ) : (
            <>
              <Button 
                onClick={() => handlePurchase()}
                size="lg" 
                className="w-full bg-primary hover:bg-primary/90"
                disabled={checkout.isPending}
              >
                {checkout.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    Unlock this preview + {paidPreviewsAfterUnlock} more · ${ctaPackPrice.toFixed(2)}
                  </>
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-2">
                High-resolution downloads · Custom wheel uploads · Credits never expire
              </p>
              <MiniPreviewOffer onPurchase={() => handlePurchase("mini")} disabled={checkout.isPending} />
            </>
          )}
        </div>
      </div>

      {/* Mobile Sticky CTA - Big Button for Conversions */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t z-50 shadow-lg">
        <div className="p-4 space-y-2">
          {canUnlock ? (
            <Button 
              onClick={handleUnlock} 
              size="lg" 
              className="w-full h-14 text-base font-semibold"
            >
              <Lock className="w-5 h-5 mr-2" />
              Unlock and Download
            </Button>
          ) : (
            <>
              <Button 
                onClick={() => handlePurchase()}
                size="lg" 
                className="w-full h-14 text-base font-semibold bg-primary hover:bg-primary/90"
                disabled={checkout.isPending}
              >
                {checkout.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-2" />
                    Processing...
                  </>
                ) : (
                  `Unlock this preview + ${paidPreviewsAfterUnlock} more · $${ctaPackPrice.toFixed(2)}`
                )}
              </Button>
              {isPromoEnabled && (
                <p className="text-xs text-center text-amber-300 font-medium">
                  🎁 Today only: {promoCredits} credits instead of {baseCredits}
                </p>
              )}
              <p className="text-xs text-center text-muted-foreground">
                High-resolution download · No watermark · No subscription
              </p>
              <MiniPreviewOffer onPurchase={() => handlePurchase("mini")} disabled={checkout.isPending} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
